/*
 * THE INTERVAL BEFORE THIS ONE (2026-10-19). Chris: "show arrows going up or down to indicate that that time interval is either up or down from the previous INTERVAL - so for instance, last 30 days,
 * these numbers are up or down from the LAST 30 days preceeding it." And, when asked about Today: compared with yesterday up to the same time of day.
 *
 * The previous interval is this one shifted back by the number of CALENDAR DAYS it spans, so it covers the same weekdays and ends at the same time of day. Today (midnight to now) is one day, so it is
 * compared with yesterday from midnight to this time; Last 7 Days (today and the six days before) is seven days, so it is compared with the seven days before those. Shifting by calendar days and not by
 * 86,400,000 ms keeps it on the same wall-clock times across a daylight saving change.
 *
 * Pure (no DOM, no network), so every rule can be tested.
 */
const DAY_MS = 86400000;

export interface Interval {
  startMs: number;
  endMs: number;
}

export type PreviousInterval = { ok: true; interval: Interval; spanDays: number } | { ok: false; reason: "all" | "history" | "invalid" };

const startOfDay = (ms: number): number => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);

  return d.getTime();
};

/** `ms` moved back by `days` calendar days, at the same time of day */
export const shiftBackDays = (ms: number, days: number): number => {
  const d = new Date(ms);
  d.setDate(d.getDate() - days);

  return d.getTime();
};

/** How many calendar days an interval touches, counting both its first and its last day (Today is 1, Last 7 Days is 7). */
export const spanDays = (startMs: number, endMs: number): number => Math.max(1, Math.round((startOfDay(endMs) - startOfDay(startMs)) / DAY_MS) + 1);

/**
 * The interval to compare `[startMs, endMs]` with.
 *   - "All" has nothing before it: not ok.
 *   - the previous interval must lie wholly inside the days the site has data for (`earliestMs`, the day the account began): a half-empty previous interval would make every number look as if it had
 *     doubled. Not ok ("history") until there is enough of it.
 */
export const previousInterval = (rangeName: string, startMs: number, endMs: number, earliestMs?: number | null): PreviousInterval => {
  if (rangeName === "All") return { ok: false, reason: "all" };
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return { ok: false, reason: "invalid" };
  const span = spanDays(startMs, endMs);
  const interval = { startMs: shiftBackDays(startMs, span), endMs: shiftBackDays(endMs, span) };
  if (earliestMs != null && Number.isFinite(earliestMs) && interval.startMs < startOfDay(earliestMs)) return { ok: false, reason: "history" };

  return { ok: true, interval, spanDays: span };
};

/** What the previous interval is called in a sentence ("up 12% from ..."). `ongoing` is whether the interval runs up to now (Today does; a day picked in the past does not). */
export const previousLabel = (span: number, ongoing: boolean): string => (span <= 1 ? (ongoing ? "yesterday up to this time" : "the day before") : `the previous ${span} days`);
